import { useMutation } from '@apollo/client';
import { UPDATE_CORPORATE_ACCOUNT_PREFERENCE } from '@mutations/updateCorporateAccountPreference';
import { BaseText, Toggle } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import { t } from 'i18next';
import React from 'react';
import styled from 'styled-components/native';
import { StyledWrapper } from 'styles/ContainerStyles';
import {
  Mutation,
  MutationUpdateCorporateAccountPreferenceArgs,
} from 'types/generatedSchemaTypes';
import { testIdentifiers } from 'utils/testIdentifiers';

const StyledWrapperWithLayout = styled(StyledWrapper)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-top: 15px;
  padding-bottom: 15px;
  border-top-width: 3px;
  border-bottom-width: 3px;
  border-top-color: ${({ theme }) => theme.colors.gray50};
  border-bottom-color: ${({ theme }) => theme.colors.gray50};
`;

const StyledBaseText = styled(BaseText)`
  flex: 1;
`;

type Props = {
  isCADefault: boolean;
  onToggleCompleted: (value: boolean) => void;
};

const CorporateAccountToggle: React.FC<Props> = ({
  isCADefault,
  onToggleCompleted,
}) => {
  const [updateCorporateAccountPreference] = useMutation<
    Mutation,
    MutationUpdateCorporateAccountPreferenceArgs
  >(UPDATE_CORPORATE_ACCOUNT_PREFERENCE, {
    onError: () => {
      showToast('error.corporateAccountPreferenceUpdateFailed', 'error');
    },
    onCompleted: () => {
      onToggleCompleted(!isCADefault);
    },
  });

  const handleValueChange = React.useCallback(
    async (value: boolean) => {
      try {
        await updateCorporateAccountPreference({
          variables: {
            input: {
              preferCorporateAccount: value,
            },
          },
        });
      } catch (error) {
        console.error('Error updating preference:', error);
      }
    },
    [updateCorporateAccountPreference]
  );

  return (
    <StyledWrapperWithLayout>
      <StyledBaseText testID={'corporate-account-toggle-title'}>
        {t('paymentMethods.setCorporateAccountAsDefault')}
      </StyledBaseText>

      <Toggle
        value={isCADefault}
        onValueChange={handleValueChange}
        {...testIdentifiers('prefer-corporate-account-toggle')}
        accessibilityLabel={t('paymentMethods.setCorporateAccountAsDefault')}
        accessibilityRole="switch"
        accessibilityState={{ checked: isCADefault }}
      />
    </StyledWrapperWithLayout>
  );
};

export default CorporateAccountToggle;
